import React, { useState } from 'react';
import styled from 'styled-components';
import { FaArrowLeft } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import UsersList from './UsersList';
import UserForm from './UserForm';
import { User } from '../../types/user';
import { useAuth } from '../../context/AuthContext';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  gap: 2rem;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.border};
  }
`;

const PageTitle = styled.h1`
  font-size: 1.8rem;
  font-weight: 600;
  margin: 0;
`;

const ContentContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 2rem;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const UsersManagement: React.FC = () => {
  const [showUserForm, setShowUserForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | undefined>(undefined);
  const navigate = useNavigate();
  const { supabaseUser: _supabaseUser } = useAuth();
  
  const handleAddUser = () => {
    setSelectedUser(undefined);
    setShowUserForm(true);
  };
  
  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setShowUserForm(true);
  };
  
  const handleCloseForm = () => {
    setShowUserForm(false);
    setSelectedUser(undefined);
  };
  
  const handleFormSuccess = () => {
    setShowUserForm(false);
    setSelectedUser(undefined);
    // Force le rechargement de la liste des utilisateurs
  };
  
  const handleBack = () => {
    navigate('/');
  };
  
  return (
    <PageContainer>
      <PageHeader>
        <BackButton onClick={handleBack} aria-label="Retour">
          <FaArrowLeft />
        </BackButton>
        <PageTitle>Gestion des utilisateurs</PageTitle>
      </PageHeader>
      
      <ContentContainer>
        <UsersList 
          onAddUser={handleAddUser}
          onEditUser={handleEditUser}
        />
      </ContentContainer>
      
      {showUserForm && (
        <ModalOverlay>
          <ModalContent>
            <UserForm 
              user={selectedUser}
              onCancel={handleCloseForm}
              onSuccess={handleFormSuccess}
            />
          </ModalContent>
        </ModalOverlay>
      )}
    </PageContainer>
  );
};

export default UsersManagement;

